// =====================================================================================
// INVRT Radar — web console — error-token map
//
// The API's error contract is `{ error: { token, message, correlation_id, details? } }`
// and the token is the ONLY part a client may branch on (supabase/functions/_shared/
// errors.ts says so in as many words: "never the prose that follows a token"). This
// module is the single place that turns a token into copy a person can act on.
//
// Two vocabularies feed it, exactly as the server has them:
//   1. Edge-layer tokens the api function raises itself (auth, validation, rate limits,
//      the money-path conditions a route detects before touching the database).
//   2. Database tokens the money/queue functions raise and `fromDatabaseError()` maps
//      onto the wire — every entry in that file's KNOWN_DATABASE_TOKENS table.
//
// src/test/errorTokens.test.ts asserts this map covers the server's full published list,
// so a token added upstream fails the suite here rather than reaching a customer as a
// bare snake_case string.
// =====================================================================================

export interface ErrorCopy {
  /** A short heading. Sentence case, no full stop. */
  title: string;
  /** What happened and what to do next. Full sentences. */
  body: string;
  /** True when retrying the identical request could plausibly succeed. */
  retryable: boolean;
}

/** Tokens the api function's own edge layer raises (_shared/errors.ts, RadarError statics). */
export const EDGE_TOKENS = [
  "config_missing",
  "config_invalid",
  "validation_failed",
  "unauthenticated",
  "invalid_api_key",
  "forbidden",
  "rate_limited",
  "not_implemented",
  "body_too_large",
  "not_found",
  "internal_error",
  "provider_unavailable",
  "quote_payload_mismatch",
  "suppressed_contact",
] as const;

/** Tokens the database raises that _shared/errors.ts maps onto the wire verbatim. */
export const DATABASE_TOKENS = [
  "insufficient_credits",
  // Raised by quote_create() and ledger_burn() (0026) whilst a payment dispute is open, and by
  // POST /v1/packs/checkout under the same name — one token, whichever layer refuses.
  "billing_suspended",
  "org_daily_cap_exceeded",
  "api_key_daily_cap_exceeded",
  "quote_expired",
  "quote_not_open",
  "quote_credits_mismatch",
  "quote_action_mismatch",
  "quote_org_mismatch",
  "idempotency_key_conflict",
  "rate_card_not_found",
  "api_key_revoked",
  "missing_idempotency_key",
  "org_not_found",
  "job_not_found",
  "api_key_not_found",
  "quote_not_found",
  "forbidden",
] as const;

/**
 * Tokens a route raises directly rather than through a RadarError static or the DB map.
 *
 * The seven `pack_*`/`checkout_*`/`stripe_*` tokens below are transcribed from the live
 * supabase/functions/api/routes/checkout.ts (POST /v1/packs/checkout) and confirmed against
 * that file's own x-radar-error-tokens block in routes/openapi.ts — not guessed from the
 * two examples a build brief happened to name. `stripe_not_configured` is shared verbatim
 * with the stripe-webhooks receiver (openapi.ts's own comment: "one token for 'Stripe is
 * not set up on this deployment', whichever half of the pair is asked").
 */
export const ROUTE_TOKENS = [
  "site_domain_conflict",
  "quote_unit_count_mismatch",
  // Raised by POST /v1/packs/checkout when organizations.status is not 'active'. A ROUTE token,
  // not a database one: no migration raises it — the refusal is the route's own, exactly like
  // pack_not_available beside it.
  "org_inactive",
  "pack_not_available",
  "pack_not_purchasable",
  "checkout_url_not_allowed",
  "checkout_not_configured",
  "stripe_not_configured",
  "stripe_unavailable",
  "checkout_evidence_write_failed",
  // Raised by POST /v1/sites/:id/icps and PATCH /v1/sites/:id/icps/:ordinal (routes/sites.ts,
  // icpLimitReachedError) when a site already holds its ceiling of ten ICP profiles.
  "icp_limit_reached",
  // C1 §4 (routes/sites.ts publishBannerHandler, PUBLIC routes/consent.ts): a banner publish
  // against a site that is not in radar_banner mode (409), and an unknown public key (404).
  "consent_mode_mismatch",
  "site_not_found",
  // C1 §2 (routes/keys.ts): the replacement key was minted but the old key could not be
  // revoked, so no plaintext was issued (502).
  "api_key_rotate_incomplete",
  // C1 §3 (routes/members.ts): the team roster's four refusals.
  "member_not_found",
  "member_exists",
  "last_admin",
  "invite_failed",
  // C1 §6 (routes/delivery.ts): delivery targets and attempts.
  "target_not_found",
  "attempt_not_found",
  "target_credentials_rejected",
  "target_kind_invalid",
  // C1 §5 (routes/contacts.ts, _shared/contacts/mail.ts): a contact id that does not resolve
  // for this organisation (404), and the two ways an email send can fail (503 / 502).
  "contact_not_found",
  "mail_not_configured",
  "email_send_failed",
] as const;

const COPY: Record<string, ErrorCopy> = {
  // ---- edge layer ---------------------------------------------------------------------
  config_missing: {
    title: "Radar is not fully configured",
    body: "A setting this action depends on is missing on the server. Send the correlation id below to INVRT — there is nothing to fix at your end.",
    retryable: false,
  },
  config_invalid: {
    title: "Radar is not fully configured",
    body: "A server setting holds a value Radar cannot use. Send the correlation id below to INVRT — there is nothing to fix at your end.",
    retryable: false,
  },
  validation_failed: {
    title: "Check the details you entered",
    body: "One or more fields were not accepted. The specific fields are listed below.",
    retryable: false,
  },
  unauthenticated: {
    title: "Your session has ended",
    body: "Sign in again to carry on. Nothing was charged.",
    retryable: false,
  },
  invalid_api_key: {
    title: "That credential was refused",
    body: "The API key is unknown, revoked, or belongs to another organisation.",
    retryable: false,
  },
  forbidden: {
    title: "You do not have access to this",
    body: "Your account is not a member of this organisation, or this action needs an organisation administrator. Ask an administrator on your team.",
    retryable: false,
  },
  rate_limited: {
    title: "Too many requests",
    body: "Radar has paused this credential briefly. Wait a moment, then try again — nothing was charged.",
    retryable: true,
  },
  not_implemented: {
    title: "Not available yet",
    body: "This part of Radar is not switched on for your organisation.",
    retryable: false,
  },
  body_too_large: {
    title: "That upload is too large",
    body: "Split the file into smaller batches and send them one at a time.",
    retryable: false,
  },
  not_found: {
    title: "Not found",
    body: "That record does not exist, or it belongs to another organisation.",
    retryable: false,
  },
  internal_error: {
    title: "Something went wrong at our end",
    body: "The action did not complete and nothing was charged. Try again; if it persists, send INVRT the correlation id below.",
    retryable: true,
  },
  provider_unavailable: {
    title: "The data provider did not answer",
    body: "The upstream source is temporarily unavailable, so the action did not happen and no credits moved. Try again shortly.",
    retryable: true,
  },
  quote_payload_mismatch: {
    title: "This quote was issued for different contacts",
    body: "A quote commits to one exact set of contacts. Your selection has changed since it was priced — request a fresh quote.",
    retryable: false,
  },
  suppressed_contact: {
    title: "Some of these contacts are on your suppression list",
    body: "Radar refused the whole request rather than charging you for a contact you already own. Nothing was charged. Remove the entries below from your suppression list, or deselect those contacts.",
    retryable: false,
  },

  // ---- database contract ---------------------------------------------------------------
  insufficient_credits: {
    title: "Not enough credits",
    body: "Your balance will not cover this action. Nothing was charged. Top up, or select fewer contacts.",
    retryable: false,
  },
  billing_suspended: {
    title: "Billing is on hold for this organisation",
    body: "A payment dispute has been raised against this account, so new spend and new purchases are both paused. Nothing was charged. Contact INVRT support to resolve the dispute — topping up will not lift the hold, and only an operator can.",
    retryable: false,
  },
  org_daily_cap_exceeded: {
    title: "Daily spend cap reached",
    body: "This organisation has hit its own daily credit cap. The cap resets at midnight UTC; an organisation administrator can raise it before then.",
    retryable: false,
  },
  api_key_daily_cap_exceeded: {
    title: "This credential's daily cap is spent",
    body: "The API key used for this request has hit its own daily cap. It resets at midnight UTC.",
    retryable: false,
  },
  quote_expired: {
    title: "That quote has expired",
    body: "Quotes are short-lived so the price you approve is the price in force. Request a fresh quote — nothing was charged.",
    retryable: false,
  },
  quote_not_open: {
    title: "That quote has already been used",
    body: "Each quote may be spent once. Request a fresh quote — nothing was charged twice.",
    retryable: false,
  },
  quote_credits_mismatch: {
    title: "The price has moved since this quote",
    body: "Radar re-checks the price at the moment of charging and refused rather than charging a figure you had not approved. Request a fresh quote.",
    retryable: false,
  },
  quote_action_mismatch: {
    title: "That quote is for a different action",
    body: "The quote was issued for another kind of work. Request a fresh quote for this action.",
    retryable: false,
  },
  quote_org_mismatch: {
    title: "That quote belongs to another organisation",
    body: "Switch to the organisation the quote was issued for, or request a fresh one here.",
    retryable: false,
  },
  idempotency_key_conflict: {
    title: "This request has already been recorded",
    body: "An earlier attempt with the same key has already been processed. Its result stands — you have not been charged twice.",
    retryable: false,
  },
  rate_card_not_found: {
    title: "No price is in force for this action",
    body: "Radar will not guess a figure. Contact INVRT so a rate card can be put in place.",
    retryable: false,
  },
  api_key_revoked: {
    title: "That credential has been revoked",
    body: "Revoked keys cannot be reactivated. Mint a new one.",
    retryable: false,
  },
  missing_idempotency_key: {
    title: "This action needs an idempotency key",
    body: "Radar did not send one, which is a fault at our end. Send INVRT the correlation id below.",
    retryable: false,
  },
  org_not_found: {
    title: "Organisation not found",
    body: "That organisation does not exist, or your account is no longer a member of it.",
    retryable: false,
  },
  job_not_found: {
    title: "Job not found",
    body: "That job does not exist, or it belongs to another organisation.",
    retryable: false,
  },
  api_key_not_found: {
    title: "API key not found",
    body: "That key does not exist, or it belongs to another organisation.",
    retryable: false,
  },
  quote_not_found: {
    title: "Quote not found",
    body: "That quote has been removed, or it belongs to another organisation. Request a fresh one.",
    retryable: false,
  },

  // ---- route-specific -------------------------------------------------------------------
  site_domain_conflict: {
    title: "That domain is already set up",
    body: "This organisation already has a site for that domain. Open the existing site instead of provisioning a second one.",
    retryable: false,
  },
  quote_unit_count_mismatch: {
    title: "This quote was issued for a different number of contacts",
    body: "A quote commits to an exact count as well as an exact set. Request a fresh quote for your current selection.",
    retryable: false,
  },

  // ---- packs & checkout (routes/packs.ts, routes/checkout.ts) --------------------------
  org_inactive: {
    title: "This organisation is not active",
    body: "The account has been closed or suspended, so it cannot make purchases. Nothing was charged. Contact INVRT support if you believe this is wrong.",
    retryable: false,
  },
  pack_not_available: {
    title: "That pack is not available",
    body: "This pack does not exist, is not active, or has not been priced yet for your organisation. Nothing was charged. Refresh the Credits tab and choose a current pack.",
    retryable: false,
  },
  pack_not_purchasable: {
    title: "That pack cannot be bought right now",
    body: "This pack is configured in a way Radar cannot take a payment for. Nothing was charged. This is a fault at our end — send INVRT the correlation id below.",
    retryable: false,
  },
  checkout_url_not_allowed: {
    title: "This deployment cannot return you from Stripe",
    body: "The web address Radar tried to send Stripe is not one this deployment is set up to return payers to. Nothing was charged. Send INVRT the correlation id below.",
    retryable: false,
  },
  checkout_not_configured: {
    title: "Purchasing is not enabled yet on this deployment",
    body: "This deployment has nowhere configured to return you to after paying, so checkout cannot start. Nothing was charged. Send INVRT the correlation id below.",
    retryable: false,
  },
  stripe_not_configured: {
    title: "Purchasing is not enabled yet on this deployment",
    body: "This deployment has not been connected to Stripe, so packs cannot be bought here yet. Nothing was charged. Send INVRT the correlation id below.",
    retryable: false,
  },
  stripe_unavailable: {
    title: "Stripe did not answer",
    body: "The payment provider did not accept the request. Nothing was charged. Try again shortly.",
    retryable: true,
  },
  checkout_evidence_write_failed: {
    title: "The purchase could not be started",
    body: "Radar could not record this attempt, so no payment page was issued rather than risk taking a payment it could not account for. Nothing was charged. Try again.",
    retryable: true,
  },

  // ---- sites: ideal-visitor profiles (routes/sites.ts) ---------------------------------
  icp_limit_reached: {
    title: "This site already has the maximum number of profiles",
    body: "A site can hold up to ten ideal-visitor profiles, and this one is full. Edit or delete an existing profile rather than adding another. Nothing was charged.",
    retryable: false,
  },

  // ---- consent banner (routes/sites.ts, routes/consent.ts) ------------------------------
  consent_mode_mismatch: {
    title: "This site uses its own consent platform",
    body: "A Radar banner can only be published on a site set to the Radar banner. Switch the consent mode on this site first, then publish again. Nothing was charged.",
    retryable: false,
  },
  site_not_found: {
    title: "Site not found",
    body: "No site matches that key. It may have been removed, or the key copied incorrectly. Check the site's public key in the Consent tab.",
    retryable: false,
  },

  // ---- API keys (routes/keys.ts) --------------------------------------------------------
  api_key_rotate_incomplete: {
    title: "The rotation did not complete",
    body: "A replacement key was created but the old key could not be revoked, so no new key has been issued and the old one still works. Revoke the old key by hand, then rotate or create a key again. Nothing was charged.",
    retryable: false,
  },

  // ---- team (routes/members.ts) ---------------------------------------------------------
  member_not_found: {
    title: "Member not found",
    body: "That person is no longer a member of this organisation. Refresh the list; someone may have removed them already.",
    retryable: false,
  },
  member_exists: {
    title: "Already a member",
    body: "That person already belongs to this organisation, so there is nothing to add. Change their role from the list if that is what you meant.",
    retryable: false,
  },
  last_admin: {
    title: "An organisation needs at least one administrator",
    body: "This person is the only administrator, so they cannot be demoted or removed. Make someone else an administrator first, then try again.",
    retryable: false,
  },
  invite_failed: {
    title: "The invitation email could not be sent",
    body: "Radar could not send the sign-in link, so nobody was added. Check the address and try again shortly; if it keeps failing, send INVRT the correlation id below.",
    retryable: true,
  },

  // ---- delivery targets (routes/delivery.ts) --------------------------------------------
  target_not_found: {
    title: "Delivery target not found",
    body: "That target does not exist, or it belongs to another organisation. Refresh the list; it may have been deleted.",
    retryable: false,
  },
  attempt_not_found: {
    title: "Delivery attempt not found",
    body: "That attempt does not exist, or it belongs to another organisation. Refresh the list and try again.",
    retryable: false,
  },
  target_credentials_rejected: {
    title: "The destination refused the credentials",
    body: "Radar checked the token with the destination before saving and it was turned down, so nothing was stored. Check the token and its permissions, then try again.",
    retryable: false,
  },
  target_kind_invalid: {
    title: "That destination is not one Radar can deliver to",
    body: "The target kind is not one this version of Radar knows. Choose email, HubSpot, Pipedrive, INVRT CRM or MONTI.",
    retryable: false,
  },

  // ---- contacts (routes/contacts.ts) ----------------------------------------------------
  contact_not_found: {
    title: "Contact not found",
    body: "One of the contacts you chose no longer exists, or it belongs to another organisation. Nothing was sent. Refresh the list and choose again.",
    retryable: false,
  },
  mail_not_configured: {
    title: "Email sending is not set up on this deployment",
    body: "Radar has no outgoing mail service configured, so nothing was sent. Download the CSV instead, or push the contacts to a delivery target. Send INVRT the correlation id below.",
    retryable: false,
  },
  email_send_failed: {
    title: "The email could not be sent",
    body: "The mail service did not accept the message, so nothing reached the addresses you entered. Try again shortly; if it keeps failing, send INVRT the correlation id below.",
    retryable: true,
  },
};

const UNKNOWN: ErrorCopy = {
  title: "Something went wrong",
  body: "Radar could not complete that action. Nothing was charged unless the message below says otherwise. Send INVRT the correlation id if it happens again.",
  retryable: true,
};

/**
 * Maps a stable error token onto human copy. An unrecognised token — a server that has
 * moved ahead of this build — degrades to honest generic copy rather than showing a
 * customer a raw snake_case string.
 */
export function copyForToken(token: string | undefined): ErrorCopy {
  if (!token) return UNKNOWN;
  return COPY[token] ?? UNKNOWN;
}

/** True when this build recognises the token. Used by the completeness test. */
export function hasCopyForToken(token: string): boolean {
  return Object.prototype.hasOwnProperty.call(COPY, token);
}

/** Every token this build knows about, sorted. Used by the completeness test. */
export function knownTokens(): string[] {
  return Object.keys(COPY).sort();
}
